import { Runtime } from "@observablehq/runtime";
import * as im from "immutable";

import { generateShortUuid } from "@moment/api-collab";
import { type PageStorageBridge } from "@moment/api-collab/page-local-storage/client";

import {
	type Cell,
	type CellInfoExecutable,
	type CellMap,
	equals as cellEquals,
} from "~/models/cell";
import {
	type AnonymousCell,
	type Cell as CellType,
	type EmptyCell,
	type ImportDeclaration,
	type NamedCell,
	type ViewOfCell,
} from "~/runtime/cell-compiler";
import {
	type ImportFromLocal,
	type ImportFromMomentId,
	type ImportFromMomentNamespace,
} from "~/runtime/import";
import { type MomentStdLibOptions, momentStdLib } from "~/runtime/moment-std-lib/momentStdLib";
import type { Env } from "~/utils/common";

import { CellHandle } from "./CellHandle";
import { type Snippet, SnippetHandle } from "./InspectFieldCodeSnippetHandle";
import { type Disposable, type ObservableModule } from "./interfaces";

/**
 * Manages the execution of cells in a canvas persistently and independently of the UI. This allows
 * React display components to mount and unmount arbitrarily, subscribing to updates from a cell,
 * and retrieving the current results any time they are needed.
 *
 * It works like this:
 *
 *   - Cells registered with the CanvasRuntimeManger must have a unique, `id` field.
 *   - A "handle" to a cell can be retrieved with the ID.
 *   - The "handle" provides the last update to the cell, and also allows cells to "subscribe" an
 *     observer to receive updates continuously.
 *   - Cells are continuously executed in the runtime until they are manually destroyed with the
 *     `dispose()` method, or the entire CanvasRuntimeManager is disposed with its `dispose()`
 *     method.
 */
export class CanvasRuntimeManager implements Disposable {
	// todo: actual types
	private readonly _runtime: Runtime;
	private readonly _mainModule: ObservableModule;
	private _cache = im.Map<string, CellHandle>();
	private _snippets = im.Map<string, SnippetHandle<object>>();

	env: Env;
	instanceID: string;

	constructor(
		env: Env,
		pageStorage: PageStorageBridge,
		documentInteractionMode: "static" | "interactive",
		currentUser: MomentStdLibOptions["currentUser"],
		baseStream: MomentStdLibOptions["baseStream"],
		query?: MomentStdLibOptions["query"],
		mutation?: MomentStdLibOptions["mutation"]
	) {
		this.env = env;
		this._runtime = new Runtime(
			momentStdLib({
				env,
				pageStorage,
				runtime: this,
				documentInteractionMode,
				currentUser,
				baseStream,
				query,
				mutation,
			})
		);
		this._mainModule = this._runtime.module();

		// assign an instance ID to the runtime when it's created
		this.instanceID = generateShortUuid();
	}

	get cacheSnapshot() {
		return this._cache;
	}

	dispose = () => {
		for (const cellHandle of this._cache.values()) {
			cellHandle.dispose();
		}

		for (const snippetHandle of this._snippets.values()) {
			snippetHandle.dispose();
		}

		this._cache = this._cache.clear();
		this._runtime.dispose();
	};

	purgeAll = (cellIds: string[]) => {
		for (const cellId of cellIds) {
			const cellHandle = this._cache.get(cellId);

			if (cellHandle === undefined) {
				return;
			}

			this.purgeCachedCell(cellHandle.cell);
		}
	};

	cacheAll = (cells: CellInfoExecutable[]) => {
		for (const cell of cells) {
			this.getOrCacheCell(cell);
		}
	};

	ensureCells = (cells: CellMap) => {
		const oldCellIds = im.Set(this._cache.keys());
		const newCellIds = im.Set(Object.keys(cells));

		/* cells no longer included need to be purged */
		const toDelete = oldCellIds.subtract(newCellIds);

		if (toDelete.size > 0) {
			this.purgeAll(toDelete.toList().toJS());
		}

		/* newCells need to be added */
		const toAdd = newCellIds
			.filter((id) => {
				return cells[id]?.status === "executable";
			})
			.subtract(oldCellIds);

		if (toAdd.size > 0) {
			this.cacheAll(
				toAdd
					.flatMap((id) => {
						const cell = cells[id];
						if (cell?.status === "executable") {
							return [cell];
						}

						return [];
					})
					.toList()
					.toArray()
			);
		}
	};

	getCell = (cell: Cell) => this._cache.get(cell.id);

	getOrCacheCell = (cellInfo: CellInfoExecutable): CellHandle => {
		const { cell: cell } = cellInfo;

		const previousCell = this._cache.get(cell.id);
		if (this._cache.has(cell.id) && previousCell) {
			//
			// If the code has not changed, just return it.
			//

			if (cellEquals(previousCell.cell, cell)) {
				return previousCell;
			}

			const cellHandle = previousCell.updateCell(cellInfo);

			this._cache = this._cache.set(cell.id, cellHandle);

			return cellHandle;
		}
		const cellHandle = CellHandle.fromCellInfo(cellInfo, this._runtime, this._mainModule);

		this._cache = this._cache.set(cell.id, cellHandle);
		return cellHandle;
	};

	purgeCachedCell = (cell: Cell) => {
		const cellHandle = this._cache.get(cell.id);
		cellHandle?.dispose();
		this._cache = this._cache.remove(cell.id);
	};

	getOrCacheSnippet = (snippet: Snippet) => {
		const { id } = snippet;

		const previousSnippet = this._snippets.get(id);
		if (this._snippets.has(id) && previousSnippet) {
			// If the code has not changed, just return it.
			if (!previousSnippet.differentFrom(snippet)) {
				return previousSnippet;
			}

			const fieldCodeHandle = previousSnippet.update(snippet);
			this._snippets = this._snippets.set(id, fieldCodeHandle);

			return fieldCodeHandle;
		}

		const fieldCodeHandle = new SnippetHandle(snippet, this._runtime, this._mainModule);
		this._snippets = this._snippets.set(id, fieldCodeHandle);

		return fieldCodeHandle;
	};

	purgeCachedSnippet = (snippet: Snippet) => {
		const fieldCodeHandle = this._snippets.get(snippet.id);
		fieldCodeHandle?.dispose();
		this._snippets = this._snippets.remove(snippet.id);
	};
}

/**
 * Callbacks for the CellDispatcher. Allows users to specify actions to do when the CellDispatcher
 * encounters cells of various types, e.g., a named cell.
 */
export interface DispatcherActions<T> {
	onMomentNamespaceImport: (
		parsed: CellType,
		declaration: ImportDeclaration,
		arg: ImportFromMomentNamespace
	) => T;
	onMomentIdImport: (
		parsed: CellType,
		declaration: ImportDeclaration,
		arg: ImportFromMomentId
	) => T;
	onLocalImport: (parsed: CellType, declaration: ImportDeclaration, arg: ImportFromLocal) => T;
	onAnonymousCell: (parsed: AnonymousCell, refs: string[]) => T;
	onEmptyCell: (parsed: EmptyCell | undefined | null, refs: string[]) => T;
	onNamedCell: (parsed: NamedCell, refs: string[]) => T;
	onViewOfCell: (parsed: ViewOfCell, refs: string[]) => T;
	onStaticError: (staticError: Error) => T;
}
